import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Badge } from './ui/badge';
import { Package, Calendar, Hash, Activity, Cpu, Table, Video, Monitor, BarChart3, Gauge, AlertTriangle, Heart, ChevronDown, ChevronRight, XCircle, AlertCircle, Info, Clock } from 'lucide-react';
import DeviceRouteMap from './DeviceRouteMap';
import OnEventTelemetryViewer from './OnEventTelemetryViewer';

// Format Unix timestamp to UTC and GMT-3
const formatHealthTimestamp = (unixTimestamp) => {
  if (!unixTimestamp) return null;
  
  const date = new Date(unixTimestamp * 1000);
  
  const utcStr = date.toISOString().replace('T', ' ').substring(0, 19) + ' UTC';
  
  // GMT-3 (subtract 3 hours from UTC)
  const gmt3Date = new Date(date.getTime() - (3 * 60 * 60 * 1000));
  const gmt3Str = gmt3Date.toISOString().replace('T', ' ').substring(0, 19) + ' GMT-3';
  
  return { utc: utcStr, gmt3: gmt3Str };
};

// State colors for video monitor
const STATE_COLORS = {
  'enabled': '#22c55e',
  'disabled': '#6b7280',
  'videoLoss': '#ef4444',
  'unknown': '#f97316'
};

// Severity configuration
const SEVERITY_CONFIG = {
  'ERROR': { color: 'bg-red-50 border-red-200', icon: XCircle, iconColor: 'text-red-500', badgeClass: 'bg-red-600', order: 1 },
  'WARNING': { color: 'bg-orange-50 border-orange-200', icon: AlertTriangle, iconColor: 'text-orange-500', badgeClass: 'bg-orange-500', order: 2 },
  'INFO': { color: 'bg-blue-50 border-blue-200', icon: Info, iconColor: 'text-blue-500', badgeClass: 'bg-blue-500', order: 3 }
};

const AdvancedResultsDisplay = ({ results }) => {
  const [expandedHealthDevices, setExpandedHealthDevices] = useState({});
  const [expandedDeviceCards, setExpandedDeviceCards] = useState({});
  const [deviceActiveTelemetryPoints, setDeviceActiveTelemetryPoints] = useState({});

  // Check if ONLY provisioning analyzer was used
  const isOnlyProvisioningAnalysis = () => {
    if (!results.results || results.results.length === 0) return false;
    return results.results.every(deviceResult => 
      deviceResult.analyses?.length === 1 && 
      deviceResult.analyses[0].analyzer === 'ProvisioningAnalyzer'
    );
  };

  // Check if ONLY video monitor analyzer was used
  const isOnlyVideoMonitorAnalysis = () => {
    if (!results.results || results.results.length === 0) return false;
    return results.results.every(deviceResult => 
      deviceResult.analyses?.length === 1 && 
      deviceResult.analyses[0].analyzer === 'VideoMonitorAnalyzer'
    );
  };

  // Check if ONLY general analyzer was used
  const isOnlyGeneralAnalysis = () => {
    if (!results.results || results.results.length === 0) return false;
    return results.results.every(deviceResult => 
      deviceResult.analyses?.length === 1 && 
      deviceResult.analyses[0].analyzer === 'GeneralAnalyzer'
    );
  };

  // Check if ONLY lowcpuidle analyzer was used
  const isOnlyLowCpuIdleAnalysis = () => {
    if (!results.results || results.results.length === 0) return false;
    return results.results.every(deviceResult =>
      deviceResult.analyses?.length === 1 &&
      deviceResult.analyses[0].analyzer === 'LowCpuIdleAnalyzer'
    );
  };

  // Check if ONLY msg sequential analyzer was used
  const isOnlyMsgSequentialAnalysis = () => {
    if (!results.results || results.results.length === 0) return false;
    return results.results.every(deviceResult =>
      deviceResult.analyses?.length === 1 &&
      deviceResult.analyses[0].analyzer === 'MsgSequentialAnalyzer'
    );
  };

  // Get all msg sequential analyzer data
  const getAllMsgSequentialData = () => {
    return results.results
      .map(deviceResult => {
        const analysis = deviceResult.analyses?.find(a => a.analyzer === 'MsgSequentialAnalyzer');
        return analysis?.output;
      })
      .filter(Boolean);
  };

  // Get all general analyzer data
  const getAllGeneralData = () => {
    return results.results
      .map(deviceResult => {
        const analysis = deviceResult.analyses?.find(a => a.analyzer === 'GeneralAnalyzer');
        return analysis?.output;
      })
      .filter(Boolean);
  };

  // Get all lowcpuidle analyzer data
  const getAllLowCpuIdleData = () => {
    return results.results
      .map(deviceResult => {
        const analysis = deviceResult.analyses?.find(a => a.analyzer === 'LowCpuIdleAnalyzer');
        return analysis?.output;
      })
      .filter(Boolean);
  };

  // Get all video monitor data for combined chart
  const getAllVideoMonitorData = () => {
    return results.results
      .map(deviceResult => {
        const analysis = deviceResult.analyses?.find(a => a.analyzer === 'VideoMonitorAnalyzer');
        return analysis?.output;
      })
      .filter(Boolean);
  };

  // Get provisioning data from analysis
  const getProvisioningData = (deviceResult) => {
    const analysis = deviceResult.analyses?.find(a => a.analyzer === 'ProvisioningAnalyzer');
    return analysis?.output || null;
  };

  // Get all provisioning data for table view
  const getAllProvisioningData = () => {
    return results.results.map(deviceResult => {
      const provData = getProvisioningData(deviceResult);
      return {
        device: deviceResult.device,
        manufacturerId: provData?.manufacturerId || 'N/A',
        image: provData?.image || 'N/A',
        baseImage: provData?.baseImage || 'N/A',
        lta: provData?.lta || 'N/A'
      };
    });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  const formatMessageData = (message) => {
    const rawMessage = message.message?.rawMessage || {};
    const { pkttype, ...data } = rawMessage;
    return data;
  };

  // Group messages by device
  const getDeviceMessages = (deviceResult) => {
    if (!results.messages) return [];
    return results.messages.filter(msg => msg.device_serial === deviceResult.device);
  };

  // Group messages by packet type
  const groupMessagesByType = (messages) => {
    const grouped = {};
    messages.forEach(msg => {
      const pkttype = msg.message?.rawMessage?.pkttype || 'unknown';
      if (!grouped[pkttype]) {
        grouped[pkttype] = [];
      }
      grouped[pkttype].push(msg);
    });
    return grouped;
  };

  // Render table view for provisioning-only analysis
  if (isOnlyProvisioningAnalysis()) {
    const provisioningData = getAllProvisioningData();
    
    return (
      <Card className="border-2">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Table className="w-6 h-6 text-primary" />
            Device Versions (v3gProvisioningInfo)
          </CardTitle>
          <CardDescription>
            Period: {results.start_date} to {results.end_date}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-muted">
                  <th className="border border-border px-4 py-3 text-left font-semibold">Device</th>
                  <th className="border border-border px-4 py-3 text-left font-semibold">Manufacturer ID</th>
                  <th className="border border-border px-4 py-3 text-left font-semibold">Image Version</th>
                  <th className="border border-border px-4 py-3 text-left font-semibold">Base Image</th>
                  <th className="border border-border px-4 py-3 text-left font-semibold">LTA Version</th>
                </tr>
              </thead>
              <tbody>
                {provisioningData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-muted/50 transition-colors">
                    <td className="border border-border px-4 py-3 font-medium">{row.device}</td>
                    <td className="border border-border px-4 py-3 font-mono text-sm">{row.manufacturerId}</td>
                    <td className="border border-border px-4 py-3 font-mono text-sm text-green-700">{row.image}</td>
                    <td className="border border-border px-4 py-3 font-mono text-sm text-green-700">{row.baseImage}</td>
                    <td className="border border-border px-4 py-3 font-mono text-sm text-blue-700">{row.lta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render general analysis with CAN statistics
  if (isOnlyGeneralAnalysis()) {
    const generalData = getAllGeneralData();
    
    // Aggregate statistics across all devices
    const aggregatedPktTypes = {};
    const aggregatedCanStats = {
      bus_speed: {},  // speed -> count of devices
      bus_active: {}, // status -> count of devices
      bus_mode: {},   // mode -> count of devices
      total_can_messages: 0,
      devices_with_can: 0
    };
    const aggregatedHealthEvents = {};
    let totalMessages = 0;

    generalData.forEach(device => {
      totalMessages += device.total_messages || 0;
      
      // Aggregate packet types
      Object.entries(device.pkttype_counts || {}).forEach(([pkt, count]) => {
        aggregatedPktTypes[pkt] = (aggregatedPktTypes[pkt] || 0) + count;
      });
      
      // Aggregate CAN stats (latest values per device)
      if (device.can_stats) {
        aggregatedCanStats.total_can_messages += device.can_stats.total_can_messages || 0;
        
        // Count devices by their latest bus_speed
        if (device.can_stats.bus_speed) {
          aggregatedCanStats.devices_with_can++;
          const speed = device.can_stats.bus_speed;
          aggregatedCanStats.bus_speed[speed] = (aggregatedCanStats.bus_speed[speed] || 0) + 1;
        }
        
        // Count devices by their latest bus_active status
        if (device.can_stats.bus_active) {
          const status = device.can_stats.bus_active;
          aggregatedCanStats.bus_active[status] = (aggregatedCanStats.bus_active[status] || 0) + 1;
        }
        
        // Count devices by their latest bus_mode
        if (device.can_stats.bus_mode) {
          const mode = device.can_stats.bus_mode;
          aggregatedCanStats.bus_mode[mode] = (aggregatedCanStats.bus_mode[mode] || 0) + 1;
        }
      }
      
      // Aggregate health events
      Object.entries(device.health_events || {}).forEach(([eventKey, data]) => {
        if (!aggregatedHealthEvents[eventKey]) {
          aggregatedHealthEvents[eventKey] = { 
            count: 0, 
            devices: [], 
            severity: data.severity || 'INFO',
            entity: data.entity || 'unknown',
            eventType: data.eventType || eventKey,
            description: data.description || '',
            lastTimestamp: data.last_timestamp || null
          };
        }
        aggregatedHealthEvents[eventKey].count += data.count;
        // Keep the most recent timestamp
        if (data.last_timestamp && (!aggregatedHealthEvents[eventKey].lastTimestamp || data.last_timestamp > aggregatedHealthEvents[eventKey].lastTimestamp)) {
          aggregatedHealthEvents[eventKey].lastTimestamp = data.last_timestamp;
        }
        aggregatedHealthEvents[eventKey].devices.push({
          device: device.device,
          count: data.count,
          status: data.status,
          lastTimestamp: data.last_timestamp
        });
      });
    });

    const sortedPktTypes = Object.entries(aggregatedPktTypes).sort((a, b) => b[1] - a[1]);
    const maxPktCount = Math.max(...Object.values(aggregatedPktTypes), 1);

    return (
      <div className="space-y-6">
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-primary" />
              General Analysis - All Devices
            </CardTitle>
            <CardDescription>
              Period: {results.start_date} to {results.end_date} | {generalData.length} devices | {totalMessages.toLocaleString()} total messages
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* CAN Statistics */}
            {aggregatedCanStats.devices_with_can > 0 && (
              <div>
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Gauge className="w-5 h-5" />
                  CAN Bus Statistics (Latest Status)
                  <Badge variant="secondary">{aggregatedCanStats.devices_with_can} devices</Badge>
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Bus Speed */}
                  {Object.keys(aggregatedCanStats.bus_speed).length > 0 && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium mb-3 text-sm text-muted-foreground">Bus Speed</h4>
                      <div className="space-y-2">
                        {Object.entries(aggregatedCanStats.bus_speed)
                          .sort((a, b) => b[1] - a[1])
                          .map(([speed, count]) => (
                            <div key={speed} className="flex justify-between items-center">
                              <span className="font-mono text-sm">{speed}</span>
                              <Badge variant="outline">{count} {count === 1 ? 'device' : 'devices'}</Badge>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Bus Active */}
                  {Object.keys(aggregatedCanStats.bus_active).length > 0 && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium mb-3 text-sm text-muted-foreground">Bus Status</h4>
                      <div className="space-y-2">
                        {Object.entries(aggregatedCanStats.bus_active)
                          .sort((a, b) => b[1] - a[1])
                          .map(([status, count]) => (
                            <div key={status} className="flex justify-between items-center">
                              <span className="font-mono text-sm">{status}</span>
                              <Badge variant={status === 'active' ? 'default' : 'destructive'}>{count} {count === 1 ? 'device' : 'devices'}</Badge>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Bus Mode */}
                  {Object.keys(aggregatedCanStats.bus_mode).length > 0 && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium mb-3 text-sm text-muted-foreground">Bus Mode</h4>
                      <div className="space-y-2">
                        {Object.entries(aggregatedCanStats.bus_mode)
                          .sort((a, b) => b[1] - a[1])
                          .map(([mode, count]) => (
                            <div key={mode} className="flex justify-between items-center">
                              <span className="font-mono text-sm text-xs">{mode}</span>
                              <Badge variant="outline">{count} {count === 1 ? 'device' : 'devices'}</Badge>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Device Health Events */}
            {Object.keys(aggregatedHealthEvents).length > 0 && (
              <div className="border-t pt-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  Device Health Events
                  <Badge variant="outline">{Object.values(aggregatedHealthEvents).reduce((sum, e) => sum + e.count, 0)} total</Badge>
                </h3>
                
                {/* ERROR and WARNING side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {['ERROR', 'WARNING'].map(severity => {
                    const eventsForSeverity = Object.entries(aggregatedHealthEvents)
                      .filter(([_, data]) => (data.severity || 'INFO') === severity);
                    
                    if (eventsForSeverity.length === 0) return null;
                    
                    const config = SEVERITY_CONFIG[severity];
                    const SeverityIcon = config.icon;
                    
                    // Group by entity within this severity
                    const entitiesMap = {};
                    eventsForSeverity.forEach(([eventKey, data]) => {
                      const entity = data.entity || 'unknown';
                      if (!entitiesMap[entity]) {
                        entitiesMap[entity] = [];
                      }
                      entitiesMap[entity].push([eventKey, data]);
                    });
                    
                    const sortedEntities = Object.keys(entitiesMap).sort();
                    
                    return (
                      <div key={severity} className={`border rounded-lg p-4 ${config.color}`}>
                        <h4 className="font-medium text-base mb-3 flex items-center gap-2">
                          <SeverityIcon className={`w-5 h-5 ${config.iconColor}`} />
                          {severity}
                          <Badge className={config.badgeClass}>
                            {eventsForSeverity.reduce((sum, [_, data]) => sum + data.count, 0)}
                          </Badge>
                        </h4>
                        
                        {sortedEntities.map(entity => {
                          const eventsForEntity = entitiesMap[entity].sort((a, b) => b[1].count - a[1].count);
                          
                          return (
                            <div key={entity} className="mb-3">
                              <h5 className="font-medium text-sm mb-2 flex items-center gap-2 text-gray-700">
                                <Activity className="w-4 h-4" />
                                {entity}
                                <Badge variant="outline" className="text-xs">
                                  {eventsForEntity.reduce((sum, [_, data]) => sum + data.count, 0)}
                                </Badge>
                              </h5>
                              
                              <div className="space-y-2 ml-4">
                                {eventsForEntity.map(([eventKey, data]) => {
                                  const isExpanded = expandedHealthDevices[eventKey];
                                  const SevIcon = config.icon;
                                  
                                  return (
                                    <div key={eventKey} className="border rounded-lg p-3 bg-white/50">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="font-medium text-sm flex items-center gap-2">
                                          <SevIcon className={`w-4 h-4 ${config.iconColor}`} />
                                          {data.eventType}
                                        </span>
                                        <Badge className={config.badgeClass}>{data.count}</Badge>
                                      </div>
                                      {data.description && (
                                        <p className="text-xs text-muted-foreground mb-2 italic">
                                          {data.description}
                                        </p>
                                      )}
                                      <div className="text-xs text-muted-foreground">
                                        Affected devices: {data.devices.length}
                                      </div>
                                      {data.lastTimestamp && (() => {
                                        const ts = formatHealthTimestamp(data.lastTimestamp);
                                        return ts && (
                                          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            <span>Last: {ts.gmt3}</span>
                                            <span className="text-gray-400">({ts.utc})</span>
                                          </div>
                                        );
                                      })()}
                                      <div className="mt-2 space-y-1">
                                        {data.devices.slice(0, isExpanded ? undefined : 3).map((d, i) => (
                                          <div key={i} className="text-xs flex justify-between">
                                            <span className="font-mono">{d.device}</span>
                                            <span>{d.count}x {d.status && `(${d.status})`}</span>
                                          </div>
                                        ))}
                                        {data.devices.length > 3 && (
                                          <button
                                            onClick={() => setExpandedHealthDevices(prev => ({
                                              ...prev,
                                              [eventKey]: !prev[eventKey]
                                            }))}
                                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-2 cursor-pointer"
                                          >
                                            {isExpanded ? (
                                              <>
                                                <ChevronDown className="w-3 h-3" />
                                                Show less
                                              </>
                                            ) : (
                                              <>
                                                <ChevronRight className="w-3 h-3" />
                                                +{data.devices.length - 3} more devices
                                              </>
                                            )}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
                
                {/* INFO section below */}
                {(() => {
                  const severity = 'INFO';
                  const eventsForSeverity = Object.entries(aggregatedHealthEvents)
                    .filter(([_, data]) => (data.severity || 'INFO') === severity);
                  
                  if (eventsForSeverity.length === 0) return null;
                  
                  const config = SEVERITY_CONFIG[severity];
                  const SeverityIcon = config.icon;
                  
                  // Group by entity within this severity
                  const entitiesMap = {};
                  eventsForSeverity.forEach(([eventKey, data]) => {
                    const entity = data.entity || 'unknown';
                    if (!entitiesMap[entity]) {
                      entitiesMap[entity] = [];
                    }
                    entitiesMap[entity].push([eventKey, data]);
                  });
                  
                  const sortedEntities = Object.keys(entitiesMap).sort();
                  
                  return (
                    <div className={`border rounded-lg p-4 ${config.color}`}>
                      <h4 className="font-medium text-base mb-3 flex items-center gap-2">
                        <SeverityIcon className={`w-5 h-5 ${config.iconColor}`} />
                        {severity}
                        <Badge className={config.badgeClass}>
                          {eventsForSeverity.reduce((sum, [_, data]) => sum + data.count, 0)}
                        </Badge>
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {sortedEntities.map(entity => {
                          const eventsForEntity = entitiesMap[entity].sort((a, b) => b[1].count - a[1].count);
                          
                          return (
                            <div key={entity} className="space-y-2">
                              <h5 className="font-medium text-sm flex items-center gap-2 text-gray-700">
                                <Activity className="w-4 h-4" />
                                {entity}
                                <Badge variant="outline" className="text-xs">
                                  {eventsForEntity.reduce((sum, [_, data]) => sum + data.count, 0)}
                                </Badge>
                              </h5>
                              
                              {eventsForEntity.map(([eventKey, data]) => {
                                const isExpanded = expandedHealthDevices[eventKey];
                                const SevIcon = config.icon;
                                
                                return (
                                  <div key={eventKey} className="border rounded-lg p-3 bg-white/50 ml-4">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-medium text-sm flex items-center gap-2">
                                        <SevIcon className={`w-4 h-4 ${config.iconColor}`} />
                                        {data.eventType}
                                      </span>
                                      <Badge className={config.badgeClass}>{data.count}</Badge>
                                    </div>
                                    {data.description && (
                                      <p className="text-xs text-muted-foreground mb-2 italic">
                                        {data.description}
                                      </p>
                                    )}
                                    <div className="text-xs text-muted-foreground">
                                      Affected devices: {data.devices.length}
                                    </div>
                                    {data.lastTimestamp && (() => {
                                      const ts = formatHealthTimestamp(data.lastTimestamp);
                                      return ts && (
                                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                          <Clock className="w-3 h-3" />
                                          <span>Last: {ts.gmt3}</span>
                                          <span className="text-gray-400">({ts.utc})</span>
                                        </div>
                                      );
                                    })()}
                                    <div className="mt-2 space-y-1">
                                      {data.devices.slice(0, isExpanded ? undefined : 3).map((d, i) => (
                                        <div key={i} className="text-xs flex justify-between">
                                          <span className="font-mono">{d.device}</span>
                                          <span>{d.count}x {d.status && `(${d.status})`}</span>
                                        </div>
                                      ))}
                                      {data.devices.length > 3 && (
                                        <button
                                          onClick={() => setExpandedHealthDevices(prev => ({
                                            ...prev,
                                            [eventKey]: !prev[eventKey]
                                          }))}
                                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-2 cursor-pointer"
                                        >
                                          {isExpanded ? (
                                            <>
                                              <ChevronDown className="w-3 h-3" />
                                              Show less
                                            </>
                                          ) : (
                                            <>
                                              <ChevronRight className="w-3 h-3" />
                                              +{data.devices.length - 3} more devices
                                            </>
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Per-Device Summary Table */}
            <div className="border-t pt-6">
              <h3 className="font-semibold text-lg mb-4">Per-Device Summary</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border border-border px-3 py-2 text-left">Device</th>
                      <th className="border border-border px-3 py-2 text-right">Messages</th>
                      <th className="border border-border px-3 py-2 text-right">CAN Msgs</th>
                      <th className="border border-border px-3 py-2 text-left">Bus Speed</th>
                      <th className="border border-border px-3 py-2 text-left">Bus Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generalData.map((device, idx) => (
                      <tr key={idx} className="hover:bg-muted/50">
                        <td className="border border-border px-3 py-2 font-mono">{device.device}</td>
                        <td className="border border-border px-3 py-2 text-right">{device.total_messages?.toLocaleString()}</td>
                        <td className="border border-border px-3 py-2 text-right">{device.can_stats?.total_can_messages || 0}</td>
                        <td className="border border-border px-3 py-2 font-mono text-xs">
                          {device.can_stats?.bus_speed || '-'}
                        </td>
                        <td className="border border-border px-3 py-2">
                          {device.can_stats?.bus_active ? (
                            <Badge variant={device.can_stats.bus_active === 'active' ? 'default' : 'destructive'} className="text-xs">
                              {device.can_stats.bus_active}
                            </Badge>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Message Types Distribution */}
            <div className="border-t pt-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Message Types Distribution
              </h3>
              <div className="space-y-2">
                {sortedPktTypes.slice(0, 15).map(([pkt, count]) => (
                  <div key={pkt} className="flex items-center gap-3">
                    <div className="w-40 text-sm font-mono text-right truncate" title={pkt}>
                      {pkt}
                    </div>
                    <div className="flex-1 h-6 bg-gray-100 rounded relative overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded transition-all duration-300 flex items-center"
                        style={{ width: `${(count / maxPktCount) * 100}%`, minWidth: '30px' }}
                      >
                        <span className="text-white text-xs font-bold px-2">{count.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="w-16 text-xs text-muted-foreground text-right">
                      {((count / totalMessages) * 100).toFixed(1)}%
                    </div>
                  </div>
                ))}
                {sortedPktTypes.length > 15 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    ... and {sortedPktTypes.length - 15} more message types
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Individual Device Cards */}
        <h2 className="text-xl font-semibold mt-8 pt-4 border-t">Individual Device Details</h2>
        {results.results.map((deviceResult, idx) => {
          const deviceMessages = getDeviceMessages(deviceResult);
          const messagesByType = groupMessagesByType(deviceMessages);
          const deviceKey = deviceResult.device;
          const isExpanded = expandedDeviceCards[deviceResult.device] || false;
          const generalAnalysis = deviceResult.analyses?.find(a => a.analyzer === 'GeneralAnalyzer');
          const positionTracks = generalAnalysis?.output?.position_tracks;
          const onEventTelemetry = generalAnalysis?.output?.on_event_telemetry || [];
          const activePoint = deviceActiveTelemetryPoints[deviceKey] || null;

          return (
            <Card key={idx} className="border-2">
              <CardHeader 
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setExpandedDeviceCards(prev => ({
                  ...prev,
                  [deviceResult.device]: !prev[deviceResult.device]
                }))}
              >
                <CardTitle className="text-2xl flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-6 h-6 text-primary" />
                  ) : (
                    <ChevronRight className="w-6 h-6 text-primary" />
                  )}
                  <Activity className="w-6 h-6 text-primary" />
                  {deviceResult.device}
                </CardTitle>
                <CardDescription className="flex items-center gap-4 mt-2 ml-8">
                  <span className="flex items-center gap-1">
                    <Hash className="w-4 h-4" />
                    Total Messages: <strong>{deviceMessages.length}</strong>
                  </span>
                </CardDescription>
              </CardHeader>

              {isExpanded && (
              <CardContent className="space-y-4">
                {/* Route map when we have simple/complete tracks */}
                {positionTracks && (
                  (positionTracks.simple?.length || positionTracks.complete?.length) ? (
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold">Route (Simple + Complete)</h3>
                      <DeviceRouteMap
                        tracks={positionTracks}
                        deviceName={deviceResult.device}
                        activePoint={activePoint}
                      />
                    </div>
                  ) : (
                    <div className="p-4 border rounded bg-muted/30 text-sm text-muted-foreground">
                      No simple or complete position messages were found for this period.
                    </div>
                  )
                )}

                {onEventTelemetry.length > 0 && (
                  <OnEventTelemetryViewer
                    events={onEventTelemetry}
                    onActivePointChange={(point) =>
                      setDeviceActiveTelemetryPoints(prev => ({
                        ...prev,
                        [deviceKey]: point || null
                      }))
                    }
                  />
                )}

                {/* Packet Type Cards */}
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">Messages by Type</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {Object.entries(messagesByType)
                      .filter(([packetType]) => packetType.toLowerCase() !== 'unknown')
                      .sort((a, b) => b[1].length - a[1].length)
                      .map(([packetType, messages]) => (
                        <Card key={packetType} className="border hover:border-primary/50 transition-colors">
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-lg flex items-center gap-2">
                                <Package className="w-5 h-5 text-primary" />
                                {packetType}
                              </CardTitle>
                              <Badge variant="secondary" className="text-base px-3 py-1">
                                {messages.length} {messages.length === 1 ? 'message' : 'messages'}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <Accordion type="single" collapsible className="w-full">
                              <AccordionItem value="messages">
                                <AccordionTrigger className="hover:no-underline">
                                  <span className="text-sm font-medium">
                                    View messages ({messages.length} total)
                                  </span>
                                </AccordionTrigger>
                                <AccordionContent>
                                  <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                                    {messages.map((msg, msgIdx) => (
                                      <div
                                        key={msgIdx}
                                        className="bg-muted/50 rounded-lg p-4 border border-border hover:border-primary/30 transition-colors"
                                      >
                                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
                                          <span className="text-xs font-mono text-muted-foreground flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {formatTimestamp(msg.message?.enqueuedTime)}
                                          </span>
                                          <Badge variant="outline" className="text-xs">
                                            Message #{msgIdx + 1}
                                          </Badge>
                                        </div>
                                        
                                        <div className="space-y-2">
                                          {Object.entries(formatMessageData(msg)).map(([key, value]) => (
                                            <div key={key} className="grid grid-cols-3 gap-2 text-sm">
                                              <span className="font-semibold text-foreground col-span-1">
                                                {key}:
                                              </span>
                                              <span className="font-mono text-muted-foreground col-span-2 break-all">
                                                {typeof value === 'object' 
                                                  ? JSON.stringify(value, null, 2)
                                                  : String(value)}
                                              </span>
                                            </div>
                                          ))}
                                        </div>

                                        <Accordion type="single" collapsible className="mt-3">
                                          <AccordionItem value="raw" className="border-0">
                                            <AccordionTrigger className="py-2 text-xs text-muted-foreground hover:text-foreground">
                                              View raw JSON
                                            </AccordionTrigger>
                                            <AccordionContent>
                                              <pre className="bg-background p-3 rounded border text-xs overflow-x-auto">
                                                {JSON.stringify(msg, null, 2)}
                                              </pre>
                                            </AccordionContent>
                                          </AccordionItem>
                                        </Accordion>
                                      </div>
                                    ))}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                </div>
              </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    );
  }

  // Render MsgSequential analysis
  if (isOnlyMsgSequentialAnalysis()) {
    const seqData = getAllMsgSequentialData();

    if (seqData.length === 0) {
      return (
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Hash className="w-6 h-6 text-primary" />
              Msg Sequential Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">No data found for the selected devices and time range.</p>
          </CardContent>
        </Card>
      );
    }

    // Collect all unique dates across all devices
    const allSeqDates = new Set();
    seqData.forEach(device => device.daily_data?.forEach(d => allSeqDates.add(d.date)));
    const sortedSeqDates = Array.from(allSeqDates).sort();

    // Summary totals across all devices
    const totalSeqMessages = seqData.reduce((s, d) => s + (d.summary?.total_messages || 0), 0);
    const totalGapEvents   = seqData.reduce((s, d) => s + (d.summary?.total_gap_events || 0), 0);
    const totalMissingSeqs = seqData.reduce((s, d) => s + (d.summary?.total_missing_seqs || 0), 0);
    const totalDuplicates  = seqData.reduce((s, d) => s + (d.summary?.total_duplicates || 0), 0);

    // Per-day totals across all devices (for the combined bar chart)
    const perDayTotals = {};
    sortedSeqDates.forEach(date => {
      perDayTotals[date] = { gap_count: 0, missing_seq_count: 0, duplicate_count: 0 };
    });
    seqData.forEach(device => {
      device.daily_data?.forEach(day => {
        if (perDayTotals[day.date]) {
          perDayTotals[day.date].gap_count       += day.gap_count;
          perDayTotals[day.date].missing_seq_count += day.missing_seq_count;
          perDayTotals[day.date].duplicate_count  += day.duplicate_count;
        }
      });
    });

    const maxGaps    = Math.max(...Object.values(perDayTotals).map(d => d.gap_count), 1);
    const maxMissing = Math.max(...Object.values(perDayTotals).map(d => d.missing_seq_count), 1);
    const maxDups    = Math.max(...Object.values(perDayTotals).map(d => d.duplicate_count), 1);

    return (
      <div className="space-y-6">
        {/* Summary Card */}
        <Card className="border-2 border-red-300 bg-red-50">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Hash className="w-6 h-6 text-red-600" />
              Msg Sequential Analysis — All Devices
            </CardTitle>
            <CardDescription>
              Period: {results.start_date} to {results.end_date} | {seqData.length} devices
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-white rounded-lg border">
                <div className="text-sm text-muted-foreground">Total Messages</div>
                <div className="text-2xl font-bold">{totalSeqMessages.toLocaleString()}</div>
              </div>
              <div className="p-4 bg-white rounded-lg border border-red-200">
                <div className="text-sm text-muted-foreground">Gap Events</div>
                <div className="text-2xl font-bold text-red-600">{totalGapEvents.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">distinct seq jumps</div>
              </div>
              <div className="p-4 bg-white rounded-lg border border-red-200">
                <div className="text-sm text-muted-foreground">Missing Seq Numbers</div>
                <div className="text-2xl font-bold text-red-600">{totalMissingSeqs.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">total across all gaps</div>
              </div>
              <div className="p-4 bg-white rounded-lg border border-yellow-200">
                <div className="text-sm text-muted-foreground">Duplicates</div>
                <div className="text-2xl font-bold text-yellow-600">{totalDuplicates.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">repeated seq numbers</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Daily Anomalies Bar Chart */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Daily Anomalies — All Devices Combined
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6 mb-4 p-2 bg-muted rounded">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-400 rounded"></div>
                <span className="text-sm">Gap Events</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-400 rounded"></div>
                <span className="text-sm">Duplicates</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-300 rounded"></div>
                <span className="text-sm">Missing Seq Count</span>
              </div>
            </div>

            <div className="space-y-2">
              {sortedSeqDates.map(date => {
                const day = perDayTotals[date];
                const hasAnomalies = day.gap_count > 0 || day.duplicate_count > 0;
                const [, mm, dd] = date.split('-');
                const displayDate = `${dd}/${mm}`;

                return (
                  <div key={date} className="flex items-center gap-3">
                    <div className="w-16 text-sm font-mono text-right">{displayDate}</div>
                    {hasAnomalies ? (
                      <>
                        <div className="flex-1 h-8 bg-gray-100 rounded relative overflow-hidden flex">
                          {day.gap_count > 0 && (
                            <div
                              className="h-full bg-red-400 flex items-center justify-center"
                              style={{ width: `${(day.gap_count / maxGaps) * 40}%`, minWidth: '28px' }}
                            >
                              <span className="text-white text-xs font-bold">{day.gap_count}</span>
                            </div>
                          )}
                          {day.duplicate_count > 0 && (
                            <div
                              className="h-full bg-yellow-400 flex items-center justify-center"
                              style={{ width: `${(day.duplicate_count / maxDups) * 20}%`, minWidth: '24px' }}
                            >
                              <span className="text-white text-xs font-bold">{day.duplicate_count}</span>
                            </div>
                          )}
                          {day.missing_seq_count > 0 && (
                            <div
                              className="h-full bg-blue-300 flex items-center pl-2"
                              style={{ width: `${(day.missing_seq_count / maxMissing) * 40}%`, minWidth: '32px' }}
                            >
                              <span className="text-blue-900 text-xs font-bold">{day.missing_seq_count}</span>
                            </div>
                          )}
                        </div>
                        <div className="w-32 text-xs text-right">
                          {day.gap_count > 0 && (
                            <span className="text-red-600 font-medium">{day.gap_count} gap{day.gap_count !== 1 ? 's' : ''}</span>
                          )}
                          {day.gap_count > 0 && day.missing_seq_count > 0 && (
                            <span className="text-muted-foreground ml-1">({day.missing_seq_count})</span>
                          )}
                          {day.duplicate_count > 0 && (
                            <span className="text-yellow-600 font-medium ml-1">{day.duplicate_count} dup{day.duplicate_count !== 1 ? 's' : ''}</span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 h-8 bg-gray-100 rounded flex items-center pl-3 border border-dashed border-gray-200">
                          <span className="text-green-600 text-xs font-medium">✓ no anomalies</span>
                        </div>
                        <div className="w-32 text-xs text-right text-green-600 font-medium">clean</div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Per-Device Daily Breakdown Table */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Daily Breakdown by Device
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border px-3 py-2 text-left sticky left-0 bg-muted">Device</th>
                    {sortedSeqDates.map(date => (
                      <th key={date} className="border border-border px-2 py-2 text-center min-w-[90px]">
                        {(() => { const [, mm, dd] = date.split('-'); return `${dd}/${mm}`; })()}
                      </th>
                    ))}
                    <th className="border border-border px-3 py-2 text-center bg-red-100">Gaps</th>
                    <th className="border border-border px-3 py-2 text-center bg-yellow-100">Dups</th>
                  </tr>
                </thead>
                <tbody>
                  {seqData.map((device) => (
                    <tr key={device.device} className="hover:bg-muted/50">
                      <td className="border border-border px-3 py-2 font-mono text-xs sticky left-0 bg-white">
                        {device.device}
                        <Badge variant="secondary" className="ml-2 text-xs">{device.environment}</Badge>
                      </td>
                      {sortedSeqDates.map(date => {
                        const dayData = device.daily_data?.find(d => d.date === date);
                        const gaps = dayData?.gap_count || 0;
                        const missing = dayData?.missing_seq_count || 0;
                        const dups = dayData?.duplicate_count || 0;
                        if (!dayData || (gaps === 0 && dups === 0)) {
                          return (
                            <td key={date} className="border border-border px-2 py-2 text-center text-green-600">0</td>
                          );
                        }
                        return (
                          <td key={date} className="border border-border px-2 py-2 text-center">
                            {gaps > 0 && (
                              <div>
                                <span className="text-red-600 font-bold">{gaps} gap{gaps !== 1 ? 's' : ''}</span>
                                <span className="text-xs text-muted-foreground block">({missing} missing)</span>
                              </div>
                            )}
                            {dups > 0 && (
                              <span className="text-yellow-600 font-bold block">{dups} dup{dups !== 1 ? 's' : ''}</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="border border-border px-3 py-2 text-center bg-red-50 font-bold">
                        <span className="text-red-600">{device.summary?.total_gap_events || 0}</span>
                        {(device.summary?.total_missing_seqs || 0) > 0 && (
                          <span className="text-xs text-muted-foreground block">({device.summary.total_missing_seqs} missing)</span>
                        )}
                      </td>
                      <td className="border border-border px-3 py-2 text-center bg-yellow-50 font-bold">
                        <span className={device.summary?.total_duplicates > 0 ? 'text-yellow-600' : 'text-green-600'}>
                          {device.summary?.total_duplicates || 0}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Individual Device Cards */}
        <h2 className="text-xl font-semibold mt-8 pt-4 border-t">Individual Device Details</h2>
        {seqData.map((device) => {
          const isExpanded = expandedDeviceCards[device.device] || false;
          const totalGaps = device.summary?.total_gap_events || 0;
          const totalMissing = device.summary?.total_missing_seqs || 0;
          const totalDups = device.summary?.total_duplicates || 0;

          return (
            <Card key={device.device} className="border-2">
              <CardHeader
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setExpandedDeviceCards(prev => ({
                  ...prev,
                  [device.device]: !prev[device.device]
                }))}
              >
                <CardTitle className="text-2xl flex items-center gap-2">
                  {isExpanded ? <ChevronDown className="w-6 h-6 text-primary" /> : <ChevronRight className="w-6 h-6 text-primary" />}
                  <Hash className="w-6 h-6 text-primary" />
                  {device.device}
                  <Badge variant={device.environment === 'prod' ? 'default' : 'secondary'}>{device.environment}</Badge>
                </CardTitle>
                <CardDescription className="flex items-center gap-6 mt-2 ml-8">
                  <span className="text-red-600 font-medium">{totalGaps} gap{totalGaps !== 1 ? 's' : ''} · {totalMissing} missing</span>
                  <span className="text-yellow-600 font-medium">{totalDups} duplicate{totalDups !== 1 ? 's' : ''}</span>
                </CardDescription>
              </CardHeader>

              {isExpanded && (
                <CardContent className="space-y-6">
                  {device.daily_data?.map((day) => {
                    const hasGaps = day.gaps?.length > 0;
                    const hasDups = day.duplicates?.length > 0;
                    if (!hasGaps && !hasDups) return null;

                    return (
                      <div key={day.date}>
                        <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {day.date}
                          {hasGaps && <Badge variant="destructive">{day.gap_count} gap{day.gap_count !== 1 ? 's' : ''} · {day.missing_seq_count} missing</Badge>}
                          {hasDups && <Badge className="bg-yellow-500">{day.duplicate_count} dup{day.duplicate_count !== 1 ? 's' : ''}</Badge>}
                        </h3>

                        {/* Gap Events */}
                        {hasGaps && (
                          <div className="mb-4">
                            <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                              Gap Events
                            </h4>
                            <div className="space-y-3">
                              {day.gaps.map((gap, gapIdx) => (
                                <div key={gapIdx} className="border border-red-200 rounded-lg p-4 bg-red-50">
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="font-semibold text-red-700 text-sm">
                                      seq {gap.before?.seq} → {gap.after?.seq} &nbsp;·&nbsp; {gap.missing_seqs?.length} missing
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3 text-xs">
                                    <div className="bg-white rounded-lg p-3 border border-red-100">
                                      <div className="text-muted-foreground font-semibold uppercase tracking-wide text-xs mb-2">Before · seq {gap.before?.seq}</div>
                                      <div className="grid grid-cols-3 gap-1">
                                        <span className="font-semibold text-foreground">pkttype:</span>
                                        <span className="font-mono col-span-2 break-all">{gap.before?.pkttype}</span>
                                        <span className="font-semibold text-foreground">timestamp:</span>
                                        <span className="font-mono col-span-2 break-all">{gap.before?.timestamp}</span>
                                      </div>
                                    </div>
                                    <div className="bg-white rounded-lg p-3 border border-red-100">
                                      <div className="text-muted-foreground font-semibold uppercase tracking-wide text-xs mb-2">After · seq {gap.after?.seq}</div>
                                      <div className="grid grid-cols-3 gap-1">
                                        <span className="font-semibold text-foreground">pkttype:</span>
                                        <span className="font-mono col-span-2 break-all">{gap.after?.pkttype}</span>
                                        <span className="font-semibold text-foreground">timestamp:</span>
                                        <span className="font-mono col-span-2 break-all">{gap.after?.timestamp}</span>
                                      </div>
                                    </div>
                                  </div>
                                  {gap.missing_seqs?.length > 0 && (
                                    <div className="mt-2 text-xs text-muted-foreground font-mono">
                                      Missing: {gap.missing_seqs.slice(0, 20).join(', ')}{gap.missing_seqs.length > 20 ? ` … +${gap.missing_seqs.length - 20} more` : ''}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Duplicate Seq Numbers */}
                        {hasDups && (
                          <div>
                            <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                              <AlertCircle className="w-4 h-4 text-yellow-500" />
                              Duplicate Seq Numbers
                            </h4>
                            <div className="space-y-3">
                              {day.duplicates.map((dup, dupIdx) => (
                                <div key={dupIdx} className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                                  <div className="font-semibold text-yellow-700 text-sm mb-3">
                                    seq {dup.seq} — appeared {dup.occurrences?.length}×
                                  </div>
                                  <div className="grid grid-cols-2 gap-3 text-xs">
                                    {dup.occurrences?.map((occ, occIdx) => (
                                      <div key={occIdx} className="bg-white rounded-lg p-3 border border-yellow-100">
                                        <div className="text-muted-foreground font-semibold uppercase tracking-wide text-xs mb-2">Occurrence {occIdx + 1}</div>
                                        <div className="grid grid-cols-3 gap-1">
                                          <span className="font-semibold text-foreground">pkttype:</span>
                                          <span className="font-mono col-span-2 break-all">{occ.pkttype}</span>
                                          <span className="font-semibold text-foreground">timestamp:</span>
                                          <span className="font-mono col-span-2 break-all">{occ.timestamp}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    );
  }

  // Render LowCpuIdle analysis with stacked bar charts for all devices
  if (isOnlyLowCpuIdleAnalysis()) {
    const lowCpuData = getAllLowCpuIdleData();
    
    if (lowCpuData.length === 0) {
      return (
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Cpu className="w-6 h-6 text-primary" />
              Low CPU Idle Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">No data found for the selected devices and time range.</p>
          </CardContent>
        </Card>
      );
    }

    // Get all unique dates across all devices
    const allDates = new Set();
    lowCpuData.forEach(device => {
      device.daily_data?.forEach(day => allDates.add(day.date));
    });
    const sortedDates = Array.from(allDates).sort();

    // Calculate totals across all devices
    const totalMessages = lowCpuData.reduce((sum, d) => sum + (d.summary?.total_messages || 0), 0);
    const totalLowCpu = lowCpuData.reduce((sum, d) => sum + (d.summary?.total_lowcpuidle || 0), 0);
    const overallPercentage = totalMessages > 0 ? ((totalLowCpu / totalMessages) * 100).toFixed(1) : 0;

    // Find max values for scaling
    const maxLowCpu = Math.max(...lowCpuData.flatMap(d => d.daily_data?.map(day => day.lowcpuidle) || [0]), 1);

    return (
      <div className="space-y-6">
        {/* Summary Card */}
        <Card className="border-2 border-orange-300 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Cpu className="w-6 h-6 text-orange-600" />
              Low CPU Idle Analysis - All Devices
            </CardTitle>
            <CardDescription>
              Period: {results.start_date} to {results.end_date} | {lowCpuData.length} devices
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-white rounded-lg border">
                <div className="text-sm text-muted-foreground">Total Messages</div>
                <div className="text-2xl font-bold">{totalMessages.toLocaleString()}</div>
              </div>
              <div className="p-4 bg-white rounded-lg border border-red-200">
                <div className="text-sm text-muted-foreground">Low CPU Idle Events</div>
                <div className="text-2xl font-bold text-red-600">{totalLowCpu.toLocaleString()}</div>
              </div>
              <div className="p-4 bg-white rounded-lg border">
                <div className="text-sm text-muted-foreground">Overall Percentage</div>
                <div className="text-2xl font-bold">{overallPercentage}%</div>
              </div>
              <div className="p-4 bg-white rounded-lg border">
                <div className="text-sm text-muted-foreground">Date Range</div>
                <div className="text-lg font-bold">{sortedDates.length} days</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Combined Chart - All Devices by Date */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Daily Low CPU Idle Events - All Devices Combined
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Legend */}
            <div className="flex gap-6 mb-4 p-2 bg-muted rounded">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-400 rounded"></div>
                <span className="text-sm">lowCpuIdle</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-400 rounded"></div>
                <span className="text-sm">Other Messages</span>
              </div>
            </div>

            {/* Stacked bars for each date */}
            <div className="space-y-2">
              {sortedDates.map(date => {
                // Sum across all devices for this date
                let dateTotalLowCpu = 0;
                let dateTotalOther = 0;
                lowCpuData.forEach(device => {
                  const dayData = device.daily_data?.find(d => d.date === date);
                  if (dayData) {
                    dateTotalLowCpu += dayData.lowcpuidle;
                    dateTotalOther += dayData.other;
                  }
                });
                const dateTotal = dateTotalLowCpu + dateTotalOther;
                const datePercentage = dateTotal > 0 ? ((dateTotalLowCpu / dateTotal) * 100).toFixed(1) : 0;
                const displayDate = new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

                return (
                  <div key={date} className="flex items-center gap-3">
                    <div className="w-16 text-sm font-mono text-right">{displayDate}</div>
                    <div className="flex-1 h-8 bg-gray-100 rounded relative overflow-hidden flex">
                      {dateTotalLowCpu > 0 && (
                        <div
                          className="h-full bg-red-400 flex items-center justify-center"
                          style={{ width: `${(dateTotalLowCpu / (dateTotalLowCpu + dateTotalOther)) * 100}%` }}
                        >
                          <span className="text-white text-xs font-bold">{dateTotalLowCpu}</span>
                        </div>
                      )}
                      {dateTotalOther > 0 && (
                        <div
                          className="h-full bg-blue-400 flex items-center justify-center"
                          style={{ width: `${(dateTotalOther / (dateTotalLowCpu + dateTotalOther)) * 100}%` }}
                        >
                          <span className="text-white text-xs font-bold">{dateTotalOther}</span>
                        </div>
                      )}
                    </div>
                    <div className="w-20 text-xs text-right">
                      <span className="text-red-600 font-medium">{datePercentage}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Per-Device Comparison Chart */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Low CPU Idle by Device (Total)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {lowCpuData
                .sort((a, b) => (b.summary?.percentage || 0) - (a.summary?.percentage || 0))
                .map((device, idx) => {
                  const lowCpu = device.summary?.total_lowcpuidle || 0;
                  const total = device.summary?.total_messages || 0;
                  const percentage = device.summary?.percentage || 0;
                  
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-32 text-sm font-mono text-right truncate" title={device.device}>
                        {device.device}
                      </div>
                      <div className="flex-1 h-8 bg-gray-100 rounded relative overflow-hidden flex">
                        <div
                          className="h-full bg-red-400 flex items-center"
                          style={{ width: `${(lowCpu / maxLowCpu) * 100}%`, minWidth: lowCpu > 0 ? '30px' : '0' }}
                        >
                          {lowCpu > 0 && (
                            <span className="text-white text-xs font-bold px-2">{lowCpu}</span>
                          )}
                        </div>
                      </div>
                      <div className="w-24 text-xs text-right">
                        <span className="text-red-600 font-bold">{percentage}%</span>
                        <span className="text-muted-foreground ml-1">({total})</span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>

        {/* Per-Device Daily Breakdown */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Daily Breakdown by Device
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border px-3 py-2 text-left sticky left-0 bg-muted">Device</th>
                    {sortedDates.map(date => (
                      <th key={date} className="border border-border px-2 py-2 text-center min-w-[80px]">
                        {new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                      </th>
                    ))}
                    <th className="border border-border px-3 py-2 text-center bg-orange-100">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lowCpuData.map((device, idx) => (
                    <tr key={idx} className="hover:bg-muted/50">
                      <td className="border border-border px-3 py-2 font-mono text-xs sticky left-0 bg-white">
                        {device.device}
                      </td>
                      {sortedDates.map(date => {
                        const dayData = device.daily_data?.find(d => d.date === date);
                        const lowCpu = dayData?.lowcpuidle || 0;
                        const percentage = dayData?.percentage || 0;
                        
                        return (
                          <td key={date} className="border border-border px-2 py-2 text-center">
                            {lowCpu > 0 ? (
                              <div>
                                <span className="text-red-600 font-bold">{lowCpu}</span>
                                <span className="text-xs text-muted-foreground block">({percentage}%)</span>
                              </div>
                            ) : (
                              <span className="text-green-600">0</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="border border-border px-3 py-2 text-center bg-orange-50 font-bold">
                        <span className="text-red-600">{device.summary?.total_lowcpuidle || 0}</span>
                        <span className="text-xs text-muted-foreground block">({device.summary?.percentage || 0}%)</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render video monitor timeline for video-monitor-only analysis
  if (isOnlyVideoMonitorAnalysis()) {
    const videoData = getAllVideoMonitorData();
    
    console.log('Video Monitor Data:', videoData);
    
    // If no data, show message
    if (videoData.length === 0) {
      return (
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Monitor className="w-6 h-6 text-primary" />
              Video Monitor Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">No video monitor data found for the selected devices and time range.</p>
          </CardContent>
        </Card>
      );
    }
    
    // Calculate time range across all devices
    let minTime = null;
    let maxTime = null;
    videoData.forEach(device => {
      Object.values(device.channels || {}).forEach(segments => {
        segments.forEach(seg => {
          const start = new Date(seg.start);
          const end = new Date(seg.end);
          if (!minTime || start < minTime) minTime = start;
          if (!maxTime || end > maxTime) maxTime = end;
        });
      });
    });
    
    console.log('Time range:', minTime, maxTime);

    const formatTime = (date) => {
      return date.toLocaleTimeString('en-US', {month: '2-digit', day:'2-digit', year:'2-digit', hour: '2-digit', minute: '2-digit' });
    };

    const formatDuration = (seconds) => {
      if (seconds < 60) return `${Math.round(seconds)}s`;
      if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
      return `${(seconds / 3600).toFixed(1)}h`;
    };

    // Aggregate stats across all devices
    const aggregatedStats = {};
    videoData.forEach(device => {
      Object.entries(device.state_stats || {}).forEach(([state, stats]) => {
        if (!aggregatedStats[state]) {
          aggregatedStats[state] = { count: 0, duration_seconds: 0 };
        }
        aggregatedStats[state].count += stats.count;
        aggregatedStats[state].duration_seconds += stats.duration_seconds;
      });
    });

    return (
      <Card className="border-2">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Monitor className="w-6 h-6 text-primary" />
            Video Monitor Timeline - All Devices
          </CardTitle>
          <CardDescription>
            Period: {results.start_date} to {results.end_date}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Legend */}
          <div className="flex flex-wrap gap-4 p-3 bg-muted rounded-lg">
            <span className="font-semibold text-sm">States:</span>
            {Object.entries(STATE_COLORS).map(([state, color]) => (
              <div key={state} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded" 
                  style={{ backgroundColor: color }}
                />
                <span className="text-sm">{state}</span>
              </div>
            ))}
          </div>

          {/* Aggregated Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(aggregatedStats).map(([state, stats]) => (
              <div key={state} className="p-3 rounded-lg border" style={{ borderLeftColor: STATE_COLORS[state], borderLeftWidth: '4px' }}>
                <div className="text-sm text-muted-foreground">{state}</div>
                <div className="text-lg font-semibold">{stats.count} transitions</div>
                <div className="text-sm text-muted-foreground">
                  Total: {formatDuration(stats.duration_seconds)}
                </div>
              </div>
            ))}
          </div>

          {/* Video Loss Events Chart */}
          {(() => {
            // Calculate videoLoss events per device
            const videoLossData = videoData.map(device => {
              let videoLossCount = 0;
              let totalDuration = 0;
              
              Object.values(device.channels || {}).forEach(segments => {
                segments.forEach(seg => {
                  if (seg.state === 'videoLoss') {
                    videoLossCount++;
                    const start = new Date(seg.start);
                    const end = new Date(seg.end);
                    totalDuration += (end - start) / 1000; // seconds
                  }
                });
              });
              
              return {
                device: device.device,
                count: videoLossCount,
                duration: totalDuration
              };
            }).sort((a, b) => b.count - a.count);

            const maxCount = Math.max(...videoLossData.map(d => d.count), 1);
            const totalVideoLoss = videoLossData.reduce((sum, d) => sum + d.count, 0);

            return (
              <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-red-700">
                  <Video className="w-5 h-5" />
                  Video Loss Events by Device
                  <Badge variant="destructive" className="ml-2">
                    {totalVideoLoss} total events
                  </Badge>
                </h3>

                <div className="space-y-3">
                  {videoLossData.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-28 text-sm font-mono text-right truncate" title={item.device}>
                        {item.device.slice(-8)}
                      </div>
                      <div className="flex-1 h-8 bg-gray-200 rounded relative overflow-hidden">
                        <div
                          className="h-full bg-red-500 rounded transition-all duration-300 flex items-center"
                          style={{ width: `${(item.count / maxCount) * 100}%`, minWidth: item.count > 0 ? '20px' : '0' }}
                        >
                          {item.count > 0 && (
                            <span className="text-white text-xs font-bold px-2">{item.count}</span>
                          )}
                        </div>
                      </div>
                      <div className="w-24 text-xs text-muted-foreground">
                        {item.count > 0 ? formatDuration(item.duration) : '-'}
                      </div>
                    </div>
                  ))}
                </div>

                {totalVideoLoss === 0 && (
                  <p className="text-center text-green-600 font-medium mt-4">
                    ✓ No video loss events detected!
                  </p>
                )}
              </div>
            );
          })()}

          {/* Combined Timeline - All Devices Together */}
          <div className="border-2 border-primary/30 rounded-lg p-4 bg-primary/5">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Monitor className="w-5 h-5 text-primary" />
              Combined View - All Devices
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({videoData.length} devices)
              </span>
            </h3>

            <div className="space-y-1">
              {videoData.map((device, deviceIdx) => {
                const channels = Object.entries(device.channels || {}).sort((a, b) => {
                  const numA = parseInt(a[0].replace('ch', '')) || 0;
                  const numB = parseInt(b[0].replace('ch', '')) || 0;
                  return numA - numB;
                });

                return channels.map(([channel, segments]) => (
                  <div key={`${deviceIdx}-${channel}`} className="flex items-center gap-2">
                    <div className="w-32 text-xs font-mono text-right truncate" title={`${device.device} - ${channel}`}>
                      {device.device.slice(-6)}/{channel}
                    </div>
                    <div className="flex-1 h-5 bg-gray-100 rounded relative overflow-hidden">
                      {segments.map((seg, segIdx) => {
                        const startTime = new Date(seg.start);
                        const endTime = new Date(seg.end);
                        const totalRange = maxTime - minTime || 1;
                        const left = ((startTime - minTime) / totalRange) * 100;
                        const width = Math.max(((endTime - startTime) / totalRange) * 100, 0.5);
                        
                        return (
                          <div
                            key={segIdx}
                            className="absolute h-full transition-opacity hover:opacity-80"
                            style={{
                              left: `${left}%`,
                              width: `${width}%`,
                              backgroundColor: seg.color,
                              minWidth: '2px'
                            }}
                            title={`${device.device} - ${channel}: ${seg.state} (${formatTime(startTime)} - ${formatTime(endTime)})`}
                          />
                        );
                      })}
                    </div>
                  </div>
                ));
              })}
            </div>

            {/* Time axis */}
            {minTime && maxTime && (
              <div className="flex justify-between mt-2 ml-32 pl-2 text-xs text-muted-foreground">
                <span>{formatTime(minTime)}</span>
                <span>{formatTime(new Date((minTime.getTime() + maxTime.getTime()) / 2))}</span>
                <span>{formatTime(maxTime)}</span>
              </div>
            )}
          </div>

          {/* Individual Timeline for each device */}
          <h3 className="font-semibold text-lg mt-6 pt-4 border-t">Individual Device Details</h3>
          {videoData.map((device, deviceIdx) => {
            const channels = Object.entries(device.channels || {}).sort((a, b) => {
              const numA = parseInt(a[0].replace('ch', '')) || 0;
              const numB = parseInt(b[0].replace('ch', '')) || 0;
              return numA - numB;
            });

            if (channels.length === 0) return null;

            return (
              <div key={deviceIdx} className="border rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  {device.device}
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    ({device.message_count} messages, {device.channel_count} channels)
                  </span>
                </h3>

                {/* Channel Timeline */}
                <div className="space-y-2">
                  {channels.map(([channel, segments]) => (
                    <div key={channel} className="flex items-center gap-2">
                      <div className="w-12 text-sm font-mono text-right">{channel}</div>
                      <div className="flex-1 h-6 bg-gray-100 rounded relative overflow-hidden">
                        {segments.map((seg, segIdx) => {
                          const startTime = new Date(seg.start);
                          const endTime = new Date(seg.end);
                          const totalRange = maxTime - minTime || 1;
                          const left = ((startTime - minTime) / totalRange) * 100;
                          const width = Math.max(((endTime - startTime) / totalRange) * 100, 0.5);
                          
                          return (
                            <div
                              key={segIdx}
                              className="absolute h-full transition-opacity hover:opacity-80"
                              style={{
                                left: `${left}%`,
                                width: `${width}%`,
                                backgroundColor: seg.color,
                                minWidth: '2px'
                              }}
                              title={`${seg.state}: ${formatTime(startTime)} - ${formatTime(endTime)}`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Time axis */}
                {minTime && maxTime && (
                  <div className="flex justify-between mt-2 ml-14 text-xs text-muted-foreground">
                    <span>{formatTime(minTime)}</span>
                    <span>{formatTime(new Date((minTime.getTime() + maxTime.getTime()) / 2))}</span>
                    <span>{formatTime(maxTime)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    );
  }

  // Default view for other analyzers
  return (
    <div className="space-y-6">
      {/* Device Cards */}
      {results.results.map((deviceResult, idx) => {
        const deviceMessages = getDeviceMessages(deviceResult);
        const messagesByType = groupMessagesByType(deviceMessages);

        return (
          <Card key={idx} className="border-2">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Activity className="w-6 h-6 text-primary" />
                {deviceResult.device}
              </CardTitle>
              <CardDescription className="flex items-center gap-4 mt-2">
                <span className="flex items-center gap-1">
                  <Hash className="w-4 h-4" />
                  Total Messages: <strong>{deviceMessages.length}</strong>
                </span>
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Packet Type Cards */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">Messages by Type</h3>
                <div className="grid grid-cols-1 gap-4">
                  {Object.entries(messagesByType)
                    .filter(([packetType]) => packetType.toLowerCase() !== 'unknown')
                    .sort((a, b) => b[1].length - a[1].length)
                    .map(([packetType, messages]) => (
                      <Card key={packetType} className="border hover:border-primary/50 transition-colors">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Package className="w-5 h-5 text-primary" />
                              {packetType}
                            </CardTitle>
                            <Badge variant="secondary" className="text-base px-3 py-1">
                              {messages.length} {messages.length === 1 ? 'message' : 'messages'}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="messages">
                              <AccordionTrigger className="hover:no-underline">
                                <span className="text-sm font-medium">
                                  View messages ({messages.length} total)
                                </span>
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                                  {messages.map((msg, msgIdx) => (
                                    <div
                                      key={msgIdx}
                                      className="bg-muted/50 rounded-lg p-4 border border-border hover:border-primary/30 transition-colors"
                                    >
                                      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
                                        <span className="text-xs font-mono text-muted-foreground flex items-center gap-1">
                                          <Calendar className="w-3 h-3" />
                                          {formatTimestamp(msg.message?.enqueuedTime)}
                                        </span>
                                        <Badge variant="outline" className="text-xs">
                                          Message #{msgIdx + 1}
                                        </Badge>
                                      </div>
                                      
                                      <div className="space-y-2">
                                        {Object.entries(formatMessageData(msg)).map(([key, value]) => (
                                          <div key={key} className="grid grid-cols-3 gap-2 text-sm">
                                            <span className="font-semibold text-foreground col-span-1">
                                              {key}:
                                            </span>
                                            <span className="font-mono text-muted-foreground col-span-2 break-all">
                                              {typeof value === 'object' 
                                                ? JSON.stringify(value, null, 2)
                                                : String(value)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>

                                      <Accordion type="single" collapsible className="mt-3">
                                        <AccordionItem value="raw" className="border-0">
                                          <AccordionTrigger className="py-2 text-xs text-muted-foreground hover:text-foreground">
                                            View raw JSON
                                          </AccordionTrigger>
                                          <AccordionContent>
                                            <pre className="bg-background p-3 rounded border text-xs overflow-x-auto">
                                              {JSON.stringify(msg, null, 2)}
                                            </pre>
                                          </AccordionContent>
                                        </AccordionItem>
                                      </Accordion>
                                    </div>
                                  ))}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default AdvancedResultsDisplay;
